import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, quarterlyPdfUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, LogOut, Download, ChevronDown, User as UserIcon, TrendingUp, Wallet, AlertCircle } from "lucide-react";
import NewExpenseDialog from "@/components/app/NewExpenseDialog";
import ExpenseList from "@/components/app/ExpenseList";
import QuarterStatusBar from "@/components/app/QuarterStatusBar";
import CategoryChart from "@/components/app/CategoryChart";
import SubmitPreviousQuarterBanner from "@/components/app/SubmitPreviousQuarterBanner";
import { formatMoney } from "@/lib/money";

function currentQuarter() {
  const d = new Date();
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
}

function quarterLabel(y, q) { return `Q${q} ${y}`; }
function quarterRange(y, q) {
  const start = new Date(y, (q - 1) * 3, 1);
  const end = new Date(y, q * 3, 0);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}, ${y}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [selected, setSelected] = useState(currentQuarter());
  const [openNew, setOpenNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quarterStatus, setQuarterStatus] = useState(null);

  const loadSummary = async () => {
    const res = await api.get("/summary");
    setSummary(res.data);
  };
  const loadExpenses = async () => {
    const res = await api.get(`/expenses`, { params: { year: selected.year, quarter: selected.quarter } });
    setExpenses(res.data);
  };
  const loadQuarterStatus = async () => {
    const res = await api.get(`/quarters/${selected.year}/${selected.quarter}/status`);
    setQuarterStatus(res.data);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadSummary(), loadExpenses(), loadQuarterStatus()]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [selected.year, selected.quarter]);

  const quartersAvailable = useMemo(() => {
    const set = new Set([`${selected.year}-${selected.quarter}`]);
    (summary?.quarters || []).forEach((q) => set.add(`${q.year}-${q.quarter}`));
    // Include current
    const c = currentQuarter();
    set.add(`${c.year}-${c.quarter}`);
    // Include last 8 quarters back from current
    let y = c.year, q = c.quarter;
    for (let i = 0; i < 8; i++) {
      set.add(`${y}-${q}`);
      q -= 1;
      if (q === 0) { q = 4; y -= 1; }
    }
    return Array.from(set).map((s) => {
      const [yy, qq] = s.split("-").map(Number);
      return { year: yy, quarter: qq };
    }).sort((a, b) => b.year - a.year || b.quarter - a.quarter);
  }, [summary, selected.year, selected.quarter]);

  const selectedTotal = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const onDownloadPdf = () => {
    const url = quarterlyPdfUrl(selected.year, selected.quarter);
    window.open(url, "_blank");
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onExpenseCreated = async () => {
    setOpenNew(false);
    toast.success("Expense added");
    await Promise.all([loadSummary(), loadExpenses()]);
  };

  const onExpenseDeleted = async () => {
    await Promise.all([loadSummary(), loadExpenses()]);
  };

  const onExpenseUpdated = async () => {
    await Promise.all([loadSummary(), loadExpenses()]);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-lg border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary">
            <span className="font-display text-lg tracking-tight font-semibold">PurseImburse</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setOpenNew(true)}
              className="rounded-full h-10 px-5 gap-2"
              data-testid="new-expense-btn"
            >
              <Plus className="w-4 h-4" /> New expense
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full px-2.5 py-1.5 hover:bg-muted transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                    {user?.name?.[0]?.toUpperCase() || <UserIcon className="w-4 h-4" />}
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 md:py-12 space-y-8">
        {/* Auto-nudge for previous quarter */}
        <SubmitPreviousQuarterBanner
          summary={summary}
          onSelectQuarter={(q) => setSelected(q)}
          onSubmitted={async () => { await Promise.all([loadSummary(), loadQuarterStatus()]); }}
        />

        {/* North star */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="md:col-span-2"
          >
            <Card className="p-8 border-border/60 bg-secondary/60 relative overflow-hidden" data-testid="north-star-card">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-primary/80">Current quarter · owed to you</div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-display text-6xl md:text-7xl font-light tracking-tighter text-primary tabular-nums">
                  {formatMoney(summary?.current_quarter?.total)}
                </span>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">
                {summary ? `${quarterLabel(summary.current_quarter.year, summary.current_quarter.quarter)} · ${summary.current_quarter.count} expense${summary.current_quarter.count === 1 ? "" : "s"}` : "…"}
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05, duration: 0.35 }}>
            <Card className="p-6 border-border/60 h-full">
              <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">All-time total</div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="mt-6 font-display text-3xl font-semibold tabular-nums">
                {formatMoney(summary?.total_all_time)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{summary?.count_all_time ?? 0} receipts logged</div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.35 }}>
            <Card className="p-6 border-border/60 h-full" data-testid="outstanding-card">
              <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Outstanding</div>
                <AlertCircle className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="mt-6 font-display text-3xl font-semibold tabular-nums text-destructive">
                {formatMoney(summary?.outstanding_total)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">Across unpaid quarters</div>
            </Card>
          </motion.div>
        </section>

        {/* Quarter picker + export */}
        <section className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-muted-foreground">Viewing</div>
            <div className="flex items-center gap-3">
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-foreground" data-testid="viewing-quarter-label">
                {quarterLabel(selected.year, selected.quarter)}
              </h2>
              <span className="text-sm text-muted-foreground">{quarterRange(selected.year, selected.quarter)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={`${selected.year}-${selected.quarter}`}
              onValueChange={(v) => {
                const [y, q] = v.split("-").map(Number);
                setSelected({ year: y, quarter: q });
              }}
            >
              <SelectTrigger className="w-[180px] rounded-full h-10" data-testid="quarter-select-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quartersAvailable.map((q) => {
                  const meta = summary?.quarters?.find((qs) => qs.year === q.year && qs.quarter === q.quarter);
                  const st = meta?.status || "open";
                  const dot = st === "paid" ? "bg-primary" : st === "submitted" ? "bg-accent" : "bg-muted-foreground/40";
                  return (
                    <SelectItem key={`${q.year}-${q.quarter}`} value={`${q.year}-${q.quarter}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                        {quarterLabel(q.year, q.quarter)}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={onDownloadPdf}
              className="rounded-full h-10 gap-2"
              disabled={expenses.length === 0}
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4" /> Export PDF
            </Button>
          </div>
        </section>

        {/* Quarter total callout */}
        <Card className="p-6 border-border/60 bg-accent/40 flex items-center justify-between" data-testid="quarter-total-card">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary/80">
              {quarterLabel(selected.year, selected.quarter)} · total owed
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              <div className="font-display text-3xl font-semibold text-primary tabular-nums">
                {formatMoney(selectedTotal)}
              </div>
            </div>
          </div>
          <div className="text-sm text-muted-foreground text-right">
            {expenses.length} expense{expenses.length === 1 ? "" : "s"}
          </div>
        </Card>

        {/* Quarter status action bar */}
        <QuarterStatusBar
          year={selected.year}
          quarter={selected.quarter}
          status={quarterStatus}
          onChanged={async () => {
            await Promise.all([loadQuarterStatus(), loadSummary()]);
          }}
        />

        {/* Category chart */}
        <CategoryChart expenses={expenses} quarterLabel={quarterLabel(selected.year, selected.quarter)} />

        {/* Expense list */}
        <section>
          <ExpenseList
            loading={loading}
            expenses={expenses}
            onNew={() => setOpenNew(true)}
            onDeleted={onExpenseDeleted}
            onUpdated={onExpenseUpdated}
          />
        </section>
      </main>

      <NewExpenseDialog open={openNew} onOpenChange={setOpenNew} onCreated={onExpenseCreated} />
    </div>
  );
}
